import { useGoogleLogin } from "@react-oauth/google";
import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CircularProgress from "../../components/Generic/CircularProgress";
import { GlobalContext } from "../../context";
import { googleHandler, signInHandler } from "../../context/actions/auth";
import AuthLayout from "../../layout/AuthLayouts";

export default function LoginPage() {
  const navigate = useNavigate();
  const {
    authDispatch,
    authState: { loggedIn },
  } = useContext(GlobalContext);

  const [googleLoading, setGoogleLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const login = useGoogleLogin({
    onSuccess: (res) => handleGoogleSubmit(res),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("All fields are required");
      return;
    }
    setLoading(true);
    const res = await signInHandler({ email: email.trim(), password }, authDispatch);
    setLoading(false);
    if (res) {
      setEmail("");
      setPassword("");
      navigate("/dashboard");
    }
  };

  const handleGoogleSubmit = async (data) => {
    setGoogleLoading(true);
    const res = await googleHandler({ access_token: data?.access_token }, authDispatch);
    if (res) navigate("/dashboard");
    setGoogleLoading(false);
  };

  useEffect(() => {
    if (loggedIn) navigate("/dashboard");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  return (
    <AuthLayout>
      <div className="w-full">
        
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl font-black text-gray-900 tracking-tight mb-2">
            Welcome back<span className="text-primary">.</span>
          </h1>
          <p className="text-gray-500 text-base font-light">
            Log in to continue your learning journey.
          </p>
        </div>

        {/* Google button */}
        <button
          type="button"
          onClick={() => login()}
          disabled={googleLoading}
          className="w-full flex items-center justify-center gap-3 border border-gray-200 hover:border-gray-400 bg-white hover:bg-gray-50 transition-all duration-300 py-4 text-gray-700 font-semibold text-sm mb-8"
        >
          {googleLoading ? (
            <CircularProgress />
          ) : (
            <>
              <svg viewBox="0 0 48 48" className="w-5 h-5">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.36-8.16 2.36-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Continue with Google
            </>
          )}
        </button>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-8">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-xs text-gray-400 font-medium uppercase tracking-widest">or with email</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-3">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              className="w-full border-b border-gray-300 focus:border-primary pb-3 outline-none text-gray-900 text-base font-medium bg-transparent transition-colors placeholder-gray-300"
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-[11px] font-bold uppercase tracking-widest text-gray-500">
                Password
              </label>
              <Link to="/forgot-password" className="text-[11px] font-bold text-primary hover:underline uppercase tracking-widest">
                Forgot?
              </Link>
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                autoComplete="current-password"
                // Once revealed this is a plain text field, and iOS would otherwise capitalise the
                // first letter and autocorrect the password.
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full border-b border-gray-300 focus:border-primary pb-3 outline-none text-gray-900 text-base font-medium bg-transparent transition-colors placeholder-gray-300 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-0 bottom-3 text-gray-400 hover:text-gray-700 transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                  {showPassword
                    ? <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    : <><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></>
                  }
                </svg>
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary hover:bg-[#b0451e] text-white font-bold text-base py-5 transition-colors duration-300 flex items-center justify-center gap-3 group mt-4"
          >
            {loading ? <CircularProgress /> : (
              <>
                Sign In
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4 group-hover:translate-x-1 transition-transform">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </>
            )}
          </button>
        </form>

        <p className="mt-8 text-sm text-center text-gray-500 font-medium">
          Don't have an account?{" "}
          <Link to="/sign-up" className="text-primary font-bold hover:underline">
            Create one free
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
